/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import tutorTableData from "Views/people/tutor/data/tutorTableData";
// import TestUserTableData from "Views/TestUser/data/TestUserTableData"

import IncomeBreakDownTabelData from "Views/Report/IncomeBreakDown/data/IncomeBreakDownTableData";
import { useState, useEffect } from "react";

import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import ArgonButton from "components/ArgonButton";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import { width } from "@mui/system";
import { getRevenueSnapshot } from "services/financeService";
import { CircularProgress } from "@mui/material";

function Tables() {
  const { columns, rows } = IncomeBreakDownTabelData;
  const [dob, setDob] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [revenueData, setRevenueData] = useState({
    sevenDayRevenue: { amount: 0, changePercentage: 0, previousAmount: 0 },
    twentyEightDayRevenue: { amount: 0, changePercentage: 0, previousAmount: 0 },
    yearRevenue: { amount: 0, totalCycles: 0 },
    tutorSalaryOwed: { amount: 0 },
  });

  useEffect(() => {
    fetchRevenueSnapshot();
  }, []);

  const fetchRevenueSnapshot = async () => {
    setLoading(true);
    try {
      const response = await getRevenueSnapshot();
      if (response.success) {
        setRevenueData(response.data);
      }
    } catch (error) {
      console.error("Error fetching revenue snapshot:", error);
    } finally {
      setLoading(false);
    }
  };

  const tabsData = [
    { label: "Clients", content: "" },
    { label: "Tutors", content: "" },
    { label: "Category_Standard", content: "" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={1}>
        <ArgonBox mb={1}>
          <Card
            sx={{
              width: "100%",
              margin: "20px auto",
              padding: 3,
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              background: "#fff",
            }}
          >
            {/* Header */}
            <ArgonBox px={2} py={1} borderBottom="1px solid #eee">
              <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                Revenue Overview
              </ArgonTypography>
            </ArgonBox>

            {/* Loading State */}
            {loading ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                <CircularProgress />
              </ArgonBox>
            ) : (
              <Grid container spacing={3} mt={1} mb={2}>
                {/* 7 Day Revenue */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    display="flex"
                    flexDirection="column"
                    p={2}
                    sx={{
                      backgroundColor: "rgba(0, 200, 83, 0.1)",
                      borderRadius: "12px",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="body2" fontWeight="bold" color="success">
                      7 Day Revenue
                    </ArgonTypography>
                    <ArgonTypography variant="h4" fontWeight="bold" color="dark">
                      ₹ {revenueData.sevenDayRevenue.amount.toLocaleString("en-IN")}
                    </ArgonTypography>
                    <ArgonBox display="flex" alignItems="center" mt={0.5}>
                      <ArgonTypography
                        variant="body2"
                        color={
                          revenueData.sevenDayRevenue.changePercentage >= 0 ? "success" : "error"
                        }
                        fontWeight="bold"
                      >
                        {revenueData.sevenDayRevenue.changePercentage >= 0 ? "+" : ""}
                        {revenueData.sevenDayRevenue.changePercentage}%
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="caption" color="text.secondary">
                      vs previous 7 days
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>

                {/* 28 Day Revenue */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    display="flex"
                    flexDirection="column"
                    p={2}
                    sx={{
                      backgroundColor: "rgba(244, 67, 54, 0.1)",
                      borderRadius: "12px",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="body2" fontWeight="bold" color="error">
                      28 Day Revenue
                    </ArgonTypography>
                    <ArgonTypography variant="h4" fontWeight="bold" color="dark">
                      ₹ {revenueData.twentyEightDayRevenue.amount.toLocaleString("en-IN")}
                    </ArgonTypography>
                    <ArgonBox display="flex" alignItems="center" mt={0.5}>
                      <ArgonTypography
                        variant="body2"
                        color={
                          revenueData.twentyEightDayRevenue.changePercentage >= 0
                            ? "success"
                            : "error"
                        }
                        fontWeight="bold"
                      >
                        {revenueData.twentyEightDayRevenue.changePercentage >= 0 ? "+" : ""}
                        {revenueData.twentyEightDayRevenue.changePercentage}%
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="caption" color="text.secondary">
                      vs previous 28 days
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>

                {/* Invoiced this year */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    display="flex"
                    flexDirection="column"
                    p={2}
                    sx={{
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                      borderRadius: "12px",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="body2" fontWeight="bold" color="info">
                      Invoiced this year
                    </ArgonTypography>
                    <ArgonTypography variant="h4" fontWeight="bold" color="dark">
                      ₹ {revenueData.yearRevenue.amount.toLocaleString("en-IN")}
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>

                {/* Total Tutors Salary Owed */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    display="flex"
                    flexDirection="column"
                    p={2}
                    sx={{
                      backgroundColor: "rgba(255, 193, 7, 0.15)",
                      borderRadius: "12px",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="body2" fontWeight="bold" color="warning">
                      Total Tutors Salary Owed
                    </ArgonTypography>
                    <ArgonTypography variant="h4" fontWeight="bold" color="dark">
                      ₹ {revenueData.tutorSalaryOwed.amount.toLocaleString("en-IN")}
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
              </Grid>
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
